import { z } from "zod";
import { diaDaSemana, somarDias } from "./dia.js";
/**
 * Ofensiva — a sequência de dias que cumpriram a meta (RN-17, RN-18).
 *
 * **Pura, e recebendo os dias já prontos.** É a única forma de o número ser
 * conferível: uma sequência de 40 dias é difícil de auditar contra um banco, e
 * trivial de auditar contra uma lista de seis linhas num teste.
 *
 * Duas regras, e as duas são fáceis de errar:
 *
 *  · **Meta integral** (RN-17). Presença parcial não conta. Responder 9 de 10
 *    é o mesmo que não responder, para efeito de ofensiva — o que dói, e é o
 *    ponto: a meta existe para ser cumprida, não para ser aproximada.
 *  · **Férias congelam, não quebram** (RN-18). Dia em férias não conta como
 *    falha **nem** como cumprimento: ele é pulado, e a sequência atravessa. Uma
 *    semana de férias entre dois dias cumpridos deixa a ofensiva em 2, não em 9.
 *    Vale **mesmo com a meta batida** — quem estudou de férias não construiu
 *    constância, tirou férias e estudou. Ver ADR 017.
 *  · **Descanso não quebra, e cumprido incrementa** (RN-17). Quais dias da
 *    semana são de descanso é configuração da trilha, não constante daqui
 *    (invariante 4): trilha de segunda a sexta e trilha de todo dia não têm a
 *    mesma cadência. Dia de descanso sem meta é pulado como o de férias; dia de
 *    descanso **com** a meta cumprida conta normalmente — estudar no sábado é
 *    constância a mais, não obrigação cumprida.
 *
 * O dia da semana sai do **dia civil**, que já é o dia do usuário (invariante
 * 16): `diaDaSemana` é de calendário e não toca fuso. Tirar o dia da semana do
 * instante UTC faria a regra de descanso valer três horas cedo demais, todo
 * dia.
 */
export const zDiaDaOfensiva = z.object({
    data: z.string(),
    cumpriu: z.boolean(),
    ferias: z.boolean(),
});
export const zOfensiva = z.object({
    /** Dias seguidos até hoje. Zero quando a sequência quebrou. */
    atual: z.number().int().nonnegative(),
    /** A maior sequência já alcançada, incluindo a atual. */
    recorde: z.number().int().nonnegative(),
    /** O último dia que cumpriu a meta, ou `null` se nunca houve. */
    ultimoCumprido: z.string().nullable(),
    /** `true` quando hoje está em férias. */
    emFerias: z.boolean(),
    /**
     * `true` quando hoje ainda não cumpriu mas a sequência de ontem está viva —
     * a tela usa isto para dizer "faltam N para não perder a ofensiva".
     */
    emRisco: z.boolean(),
    /** `true` quando hoje é dia de descanso da trilha (RN-17). */
    descansoHoje: z.boolean(),
});
/**
 * Calcula a ofensiva a partir dos dias do diário.
 *
 * `dias` não precisa vir ordenado nem completo: dia sem linha é dia sem
 * resposta, e quebra — a menos que esteja em férias ou seja dia de descanso.
 *
 * `diasDeDescanso` é obrigatório, e não tem padrão aqui de propósito: quem sabe
 * a cadência é a trilha. Sem padrão, chamador que ficou para trás é erro de
 * compilação, não número errado na tela.
 */
export function calcularOfensiva(dias, hoje, diasDeDescanso) {
    const porData = new Map(dias.map((d) => [d.data, d]));
    const deHoje = porData.get(hoje);
    const emFerias = deHoje?.ferias ?? false;
    const descanso = new Set(diasDeDescanso);
    const ehDescanso = (dia) => descanso.has(diaDaSemana(dia));
    const descansoHoje = ehDescanso(hoje);
    const cumpridos = dias.filter((d) => d.cumpriu).map((d) => d.data).sort();
    const ultimoCumprido = cumpridos.length > 0 ? cumpridos[cumpridos.length - 1] : null;
    /** Anda para trás a partir de `partida`, contando dias cumpridos. */
    const contarDe = (partida) => {
        let sequencia = 0;
        let dia = partida;
        // Teto de dez anos: uma trava, não uma regra de negócio. Sem ela, um dado
        // corrompido faria isto rodar para sempre.
        for (let i = 0; i < 3650; i++) {
            const d = porData.get(dia);
            // A ordem dos ramos é a regra, não arrumação. Férias vem primeiro porque
            // ela **vence** o cumprimento: a RN-18 diz que dia em férias não conta
            // como falha nem como cumprimento, e o dia estudado em férias é
            // justamente o caso em que as duas leituras divergem. O diário grava
            // `cumpriu` pela meta e `ferias` pela trilha, sem se falarem — testar
            // `cumpriu` primeiro fazia a semana de férias com estudo somar à
            // sequência, que é o oposto de congelar.
            if (d?.ferias) {
                // Pulado: não conta como falha nem como cumprimento (RN-18).
            }
            else if (d?.cumpriu) {
                sequencia += 1;
            }
            else if (ehDescanso(dia)) {
                // Descanso sem meta também é pulado (RN-17): a sequência atravessa o
                // fim de semana de quem estuda de segunda a sexta. É aqui que a
                // assimetria com férias aparece: o descanso só pula o dia **vazio**,
                // porque o ramo de cima já deixou o descanso cumprido incrementar.
            }
            else {
                break;
            }
            dia = somarDias(dia, -1);
        }
        return sequencia;
    };
    // Hoje ainda está acontecendo. Se ainda não cumpriu, a ofensiva é a de ontem
    // — a sequência só quebra quando o dia acaba sem a meta, não ao amanhecer.
    const atual = deHoje?.cumpriu ? contarDe(hoje) : contarDe(somarDias(hoje, -1));
    // Em risco só em dia que pode quebrar: no descanso não há o que perder.
    const emRisco = !deHoje?.cumpriu && !emFerias && !descansoHoje && atual > 0;
    // O recorde precisa varrer o histórico, não só a ponta: a maior sequência
    // pode ter terminado meses atrás.
    let recorde = atual;
    for (const data of cumpridos)
        recorde = Math.max(recorde, contarDe(data));
    return { atual, recorde, ultimoCumprido, emFerias, emRisco, descansoHoje };
}
/**
 * Os dias de férias que não têm linha no diário.
 *
 * `diario` só ganha linha em dia que teve resposta. Um período de férias sem
 * estudo nenhum não deixa registro, e a ofensiva leria esses dias como falha.
 * Ao desligar as férias, estas datas viram linhas com `ferias = true`.
 */
export function diasDeFeriasSemRegistro(desde, ate, jaRegistrados) {
    const existentes = new Set(jaRegistrados);
    const faltando = [];
    let dia = desde;
    for (let i = 0; i < 3650 && dia <= ate; i++) {
        if (!existentes.has(dia))
            faltando.push(dia);
        dia = somarDias(dia, 1);
    }
    return faltando;
}
//# sourceMappingURL=ofensiva.js.map